import moment from "moment";

import { doRangesOverlap, useTranslation } from "utils";

import { CropFCPO, CropFormValidationError } from "models";

const MIN_GROWING_PERIOD_WEEKS = 4;
const MAXIMUM_PERMISSIBLE_OVERLAP_IN_DAYS = 30;

export const FIELD_VALIDATION_ERROR_UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP = "FIELD_VALIDATION_ERROR_UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP";
export const FIELD_VALIDATION_ERROR_UNDERSOWING_NO_MAIN_CROP = "FIELD_VALIDATION_ERROR_UNDERSOWING_NO_MAIN_CROP";
export type useValidateCropPeriodsType = {
  validatePeriods: (currentCrop: CropFCPO, existingCrops: CropFCPO[]) => CropFormValidationError[];
};

export const useValidateCropPeriods = (): useValidateCropPeriodsType => {
  const { t } = useTranslation("allFieldView");
  const validatePeriods = (currentCrop: CropFCPO, existingCrops: CropFCPO[]) => {
    const newErrors: CropFormValidationError[] = [];

    // Check if harvest date is before seeding date
    if (moment(currentCrop.harvestEndDate).isBefore(currentCrop.seedingStartDate)) {
      newErrors.push({
        fieldId: "harvestEndDate",
        message: t("cropForm.errorMessages.invalidHarvestEndDate"),
      });
    }

    // Check if growing period is less than 4 weeks
    const growingPeriodWeeks = moment(currentCrop.harvestEndDate).diff(moment(currentCrop.seedingStartDate), "weeks");

    if (growingPeriodWeeks < MIN_GROWING_PERIOD_WEEKS) {
      newErrors.push({
        fieldId: "harvestEndDate",
        message: t("cropForm.errorMessages.invalidGrowingPeriod", { minWeeks: MIN_GROWING_PERIOD_WEEKS }),
      });
    }

    // Check for overlapping periods based on crop type
    existingCrops.forEach((existingCrop) => {
      // Skip comparison with itself
      if (existingCrop.id === currentCrop.id) return;

      // Skip validation if either crop is in a past year
      const currentYear = 2025; // intentionally hardcoded
      const currentCropYear = moment(currentCrop.harvestEndDate).year();
      const existingCropYear = moment(existingCrop.harvestEndDate).year();
      if (currentCropYear < currentYear || existingCropYear < currentYear) return;

      const rangesOverlap = doRangesOverlap(
        moment(currentCrop.seedingStartDate),
        moment(currentCrop.harvestEndDate),
        moment(existingCrop.seedingStartDate),
        moment(existingCrop.harvestEndDate),
        MAXIMUM_PERMISSIBLE_OVERLAP_IN_DAYS
      );

      if (rangesOverlap) {
        // Main crops cannot overlap with any other main crops or cover crops
        if (currentCrop.cropType === "MAIN" && (existingCrop.cropType === "MAIN" || existingCrop.cropType === "COVER")) {
          newErrors.push({
            fieldId: "seedingStartDate", // TODO (Alp): this id does not make much sense
            message: t("cropForm.errorMessages.mainCropOverlap"),
          });
        }

        // Cover crops cannot overlap with main crops or other side crops
        if (
          currentCrop.cropType === "COVER" &&
          (existingCrop.cropType === "MAIN" || existingCrop.cropType === "COVER" || existingCrop.cropType === "UNDERSOWING")
        ) {
          newErrors.push({
            fieldId: "seedingStartDate",
            message: t("cropForm.errorMessages.coverCropOverlap"),
          });
        }

        // Undersowing can overlap with main but not other side crops
        if (currentCrop.cropType === "UNDERSOWING" && (existingCrop.cropType === "COVER" || existingCrop.cropType === "UNDERSOWING")) {
          newErrors.push({
            fieldId: "seedingStartDate",
            message: t("cropForm.errorMessages.undersowingCropOverlap"),
          });
        }
      }
    });

    // Undersowing must overlap with a main crop
    if (currentCrop.cropType === "UNDERSOWING") {
      const mainCrops = existingCrops.filter((crop) => crop.cropType === "MAIN");

      if (mainCrops.length === 0) {
        newErrors.push({
          fieldId: FIELD_VALIDATION_ERROR_UNDERSOWING_NO_MAIN_CROP,
          message: t("cropForm.errorMessages.undersowingNoMainCrop"),
        });
        return newErrors;
      }

      const undersowingOverlapsMain = mainCrops.some((mainCrop) =>
        doRangesOverlap(
          moment(currentCrop.seedingStartDate),
          moment(currentCrop.harvestEndDate),
          moment(mainCrop.seedingStartDate),
          moment(mainCrop.harvestEndDate),
          MAXIMUM_PERMISSIBLE_OVERLAP_IN_DAYS
        )
      );

      if (!undersowingOverlapsMain) {
        newErrors.push({
          fieldId: FIELD_VALIDATION_ERROR_UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP,
          message: t("cropForm.errorMessages.undersowingNotCropOverlap"),
        });
      }
    }

    return newErrors;
  };

  return {
    validatePeriods,
  };
};
